/**
 * src/lib/theme.js — dashboard theme selection.
 *
 * A "theme" is nothing but the value of `data-theme` on <html>; styles.css
 * defines what each value looks like (`[data-theme="light"]`, and more to
 * come). This module just applies and remembers the choice.
 *
 * This governs the authenticated dashboard shell only. It is unrelated to
 * the per-studio --accent colour injected on public pages, which is set as
 * an inline style and always wins regardless of data-theme.
 */

const STORAGE_KEY = 'ts-theme';
export const DEFAULT_THEME = 'light';

/**
 * Themes styles.css defines, in picker order. Keep in sync with:
 *  - the `[data-theme="..."]` blocks in styles.css
 *  - VALID_THEMES in api/src/routes/artists.js (server-side validation)
 */
export const THEMES = ['light', 'dark', 'neon-ink', 'old-school-flash', 'aurora'];

/** Display name for each theme, for the picker. */
export const THEME_LABELS = {
  light: 'Light',
  dark: 'Dark',
  'neon-ink': 'Neon Ink',
  'old-school-flash': 'Old School Flash',
  aurora: 'Aurora',
};

export function getTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function setTheme(theme) {
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage unavailable (private mode, disabled) — the theme still
    // applies for this session via the DOM attribute, it just won't persist.
  }
}

/** Applies whatever theme is already stored. Call once, as early as possible. */
export function applyStoredTheme() {
  document.documentElement.dataset.theme = getTheme();
}
